import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { RefreshCw, Loader2, CheckCircle2, Inbox, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fmtDT = (v) => (v ? `${new Date(v).toLocaleDateString("it-IT")} ${new Date(v).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}` : "—");
const notify = () => window.dispatchEvent(new Event("pgp:compagnie-richieste-changed"));

function Row({ r, onEvasa, onDelete }) {
  return (
    <tr className="border-b border-slate-800/70 align-top" data-testid={`creq-row-${r.id}`}>
      <td className="py-2.5 pr-3 whitespace-nowrap text-slate-300">{fmtDT(r.created_at)}</td>
      <td className="py-2.5 pr-3">
        <div className="font-semibold text-slate-100">{r.studio_name || "—"}</div>
        <div className="text-xs text-slate-500">{[r.codice_studio && `Cod. ${r.codice_studio}`, r.richiedente_nome, r.richiedente_email].filter(Boolean).join(" · ")}</div>
      </td>
      <td className="py-2.5 pr-3 font-medium text-amber-300" data-testid={`creq-nome-${r.id}`}>{r.nome}</td>
      <td className="py-2.5 pr-3 text-xs text-slate-400 whitespace-pre-wrap max-w-sm">{r.note || "—"}</td>
      <td className="py-2.5 text-right whitespace-nowrap">
        {r.evasa ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-400 mr-2" data-testid={`creq-evasa-${r.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Evasa</span>
        ) : (
          <Button size="sm" onClick={() => onEvasa(r.id)} data-testid={`creq-segna-${r.id}`} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white mr-1">Segna come evasa</Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => onDelete(r)} data-testid={`creq-del-${r.id}`} className="h-8 text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></Button>
      </td>
    </tr>
  );
}

export default function RichiesteCompagnie() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get("/admin/compagnie-richieste").then(({ data }) => { setRows(data || []); notify(); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const evasa = async (id) => {
    try { await api.post(`/admin/compagnie-richieste/${id}/evasa`); setRows((rs) => rs.map((r) => (r.id === id ? { ...r, evasa: true } : r))); notify(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Eliminare la richiesta "${r.nome}"?`)) return;
    try { await api.delete(`/admin/compagnie-richieste/${r.id}`); setRows((rs) => rs.filter((x) => x.id !== r.id)); notify(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const nuove = rows.filter((r) => !r.evasa).length;

  return (
    <div data-testid="richieste-compagnie-page">
      <PageHeader title="Richieste Compagnie" subtitle="Richieste di nuove compagnie assicurative inviate dagli studi." icon={Inbox}
        actions={<Button onClick={load} disabled={loading} variant="outline" data-testid="creq-refresh" className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white">
          {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Aggiorna
        </Button>} />
      <div className="pgp-card p-6" data-testid="creq-list">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-head font-semibold text-slate-100">Richieste</h3>
          <span className={`text-xs rounded-full px-2 py-0.5 ${nuove ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-slate-400"}`} data-testid="creq-count-nuove">{nuove} nuove</span>
        </div>
        {!rows.length ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="creq-empty">{loading ? "Caricamento…" : "Nessuna richiesta."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                <th className="py-2 pr-3">Data e ora</th><th className="py-2 pr-3">Studio</th><th className="py-2 pr-3">Compagnia richiesta</th><th className="py-2 pr-3">Note</th><th className="py-2" />
              </tr></thead>
              <tbody>{rows.map((r) => <Row key={r.id} r={r} onEvasa={evasa} onDelete={del} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
